"use strict";
/**
 * Background Job Constants
 *
 * Enums and constants for background job system.
 * Used by frontend (polling) and backend (job execution).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.JOB_STATUS_LABELS = exports.JOB_STEP_LABELS = exports.SUB_TASK_STATUS_LABELS = exports.QVET_SYNC_REMOVAL_HELP = exports.QVET_SYNC_REMOVAL_LABELS = exports.QVET_SYNC_KIND_LABELS = exports.QVET_FRESHNESS_LABELS = exports.QVET_SYNC_WINDOW_LABELS = exports.QVET_SYNC_SCHEDULE = exports.QVET_REPORT_TREATMENT = exports.REPORT_TYPE_LABELS = exports.JOB_TYPE_LABELS = exports.JobStep = exports.JobStatus = exports.REPORT_PARAM_TYPES = exports.SubTaskStatus = exports.ReportType = exports.JobType = void 0;
exports.qvetExpectedEveryDays = qvetExpectedEveryDays;
exports.qvetScheduleLabel = qvetScheduleLabel;
exports.qvetSyncPreview = qvetSyncPreview;
exports.qvetSyncWindow = qvetSyncWindow;
exports.qvetFreshness = qvetFreshness;
/**
 * Job types for QVET sync operations
 */
var JobType;
(function (JobType) {
    JobType["SYNC_PURCHASES"] = "SYNC_PURCHASES";
    JobType["SYNC_INVENTORY"] = "SYNC_INVENTORY";
    JobType["SYNC_TRANSFERS"] = "SYNC_TRANSFERS";
    JobType["SYNC_MOVEMENTS"] = "SYNC_MOVEMENTS";
    JobType["SYNC_COLLECTIONS"] = "SYNC_COLLECTIONS";
    JobType["SYNC_CASH_CLOSINGS"] = "SYNC_CASH_CLOSINGS";
    JobType["SYNC_CASH_FLOWS"] = "SYNC_CASH_FLOWS";
    JobType["SYNC_PURCHASE_INVOICES"] = "SYNC_PURCHASE_INVOICES";
    JobType["SYNC_DELIVERY_NOTES"] = "SYNC_DELIVERY_NOTES";
    JobType["SYNC_PURCHASE_ORDERS"] = "SYNC_PURCHASE_ORDERS";
    JobType["SYNC_CATALOG"] = "SYNC_CATALOG";
    JobType["SYNC_SUPPLIERS"] = "SYNC_SUPPLIERS";
    JobType["SYNC_CLIENTS"] = "SYNC_CLIENTS";
    JobType["SYNC_INVENTORY_SNAPSHOT"] = "SYNC_INVENTORY_SNAPSHOT";
    JobType["SYNC_COLLECTION_DETAILS"] = "SYNC_COLLECTION_DETAILS";
    JobType["SYNC_SALE_DETAILS"] = "SYNC_SALE_DETAILS";
    JobType["SYNC_PAYABLES"] = "SYNC_PAYABLES";
    JobType["SYNC_STAFF"] = "SYNC_STAFF";
    JobType["SYNC_PETS"] = "SYNC_PETS";
    JobType["SYNC_VISITS"] = "SYNC_VISITS";
    JobType["SYNC_VISIT_DIAGNOSES"] = "SYNC_VISIT_DIAGNOSES";
    JobType["SYNC_SECTIONS"] = "SYNC_SECTIONS";
    JobType["SYNC_WAREHOUSES"] = "SYNC_WAREHOUSES";
    JobType["SYNC_CATALOGS"] = "SYNC_CATALOGS";
    JobType["SYNC_BATCH"] = "SYNC_BATCH";
    JobType["SYNC_ALL"] = "SYNC_ALL";
    JobType["SYNC_FACTURAMA_PRODUCTS"] = "SYNC_FACTURAMA_PRODUCTS";
    JobType["SYNC_GOOGLE_CONTACTS"] = "SYNC_GOOGLE_CONTACTS";
    /** Fetch supplier price lists (Telvet, Difarvet) — GH518. Not a QVET sync. */
    JobType["SYNC_SUPPLIER_PRICES"] = "SYNC_SUPPLIER_PRICES";
    /**
     * Push pending catalog changes **into** QVET — GH527. The only job that writes to
     * QVET rather than reading from it, which is why it runs manager-only and simulates
     * before it writes.
     */
    JobType["PUSH_CATALOG_TO_QVET"] = "PUSH_CATALOG_TO_QVET";
    /**
     * The catalog's **full sync** — GH527 · SP11 P3. Reads every article's ficha through the API,
     * and the HTML ficha only when the API says it is a medicine or has an active promotion.
     * Monthly, from a button in the catalog module, and once at deploy time (the initial sync).
     * Resumable: a per-article ledger survives a dyno restart.
     */
    JobType["SYNC_CATALOG_FULL"] = "SYNC_CATALOG_FULL";
    /**
     * Mirror the prescriptions already issued on the SENASICA SITPV portal — GH535 · Paso 10. Not a QVET
     * sync: reads only (periods → prescriptions → detail → additional info) and never deletes.
     */
    JobType["SYNC_SITPV_PRESCRIPTIONS"] = "SYNC_SITPV_PRESCRIPTIONS";
})(JobType || (exports.JobType = JobType = {}));
/**
 * Report types for batch sync operations.
 * Each report type has specific parameter requirements.
 */
var ReportType;
(function (ReportType) {
    /** Purchases report - uses date range params */
    ReportType["PURCHASES"] = "PURCHASES";
    /** Inventory movements report - uses date range params */
    ReportType["MOVEMENTS"] = "MOVEMENTS";
    /** Transfers report - uses date range params */
    ReportType["TRANSFERS"] = "TRANSFERS";
    /** Collections (Cobros) report - uses date range params */
    ReportType["COLLECTIONS"] = "COLLECTIONS";
    /** Cash closings (Cierres de Caja) report - uses date range params */
    ReportType["CASH_CLOSINGS"] = "CASH_CLOSINGS";
    /** Cash flows (Entradas/Salidas de Caja) report - uses date range params */
    ReportType["CASH_FLOWS"] = "CASH_FLOWS";
    /** Purchase invoices (Facturas Compra) report - uses date range params */
    ReportType["PURCHASE_INVOICES"] = "PURCHASE_INVOICES";
    ReportType["DELIVERY_NOTES"] = "DELIVERY_NOTES";
    /** Purchase orders (Pedidos) report - uses date range params */
    ReportType["PURCHASE_ORDERS"] = "PURCHASE_ORDERS";
    /** Inventory snapshot - uses snapshot date param */
    ReportType["INVENTORY_SNAPSHOT"] = "INVENTORY_SNAPSHOT";
    /** Clients list - no date params required */
    ReportType["CLIENTS"] = "CLIENTS";
    /** Product catalog - no date params required */
    ReportType["CATALOG"] = "CATALOG";
    /** Suppliers catalog - no date params required */
    ReportType["SUPPLIERS"] = "SUPPLIERS";
    /** Collection details (Cobros detalle) - uses date range params */
    ReportType["COLLECTION_DETAILS"] = "COLLECTION_DETAILS";
    /** Sale details (Detalle ventas, Report 89) - uses date range params */
    ReportType["SALE_DETAILS"] = "SALE_DETAILS";
    /** Payables / Accounts payable (Vencimientos, Report 718) - uses date range params */
    ReportType["PAYABLES"] = "PAYABLES";
    /** Staff / Personnel catalog (Report 102) - no date params required */
    ReportType["STAFF"] = "STAFF";
    /** Pets / Animals catalog (listadoMascotas) - no date params required */
    ReportType["PETS"] = "PETS";
    /** Visits / Consultations (listadoVisitas) - uses date range params */
    ReportType["VISITS"] = "VISITS";
    /**
     * Diagnoses per visit (Report 43 «Listado de visitas con diagnósticos») - uses date range params.
     * One row per visit + diagnosis; joined to visits by the QVET visit id. Added 2026-10-25 (GH45 SP4).
     */
    ReportType["VISIT_DIAGNOSES"] = "VISIT_DIAGNOSES";
    /**
     * 🆕 Sections catalogue — no date params. **Writes our own `sections` model** (class C).
     *
     * It ran in the nightly batch and was **absent from the registry**, so the panel could neither
     * show it nor launch it. Two of the five syncs that write into our own models were exactly the
     * two you could not see. Added 2026-09-12 · SP10.
     */
    ReportType["SECTIONS"] = "SECTIONS";
    /** 🆕 Warehouses catalogue — no date params. **Writes our own `warehouses` model** (class C). */
    ReportType["WAREHOUSES"] = "WAREHOUSES";
    /**
     * 🆕 QVET's **parameter catalogues** — brands, VAT types, units, routes of administration,
     * active ingredients, families, subfamilies, centres… **20 lists in one pass** (class A).
     *
     * Same story as sections and warehouses: it ran nightly and was absent from the registry, so
     * the panel could not show it. It is the biggest blind spot of the three — behind an article
     * alone there are around ten of these. Added 2026-09-12 · SP10.
     */
    ReportType["CATALOGS"] = "CATALOGS";
    /**
     * 🆕 **The collaborator ↔ QVET staff links.** Writes `collaborators` — a model of ours.
     *
     * > *«El personal no es el mismo bloque que otros. El personal también sincroniza nuestros
     * > collaborators.»* — Javier, 2026-09-12
     *
     * He is right, and it is a **different sync** from {@link ReportType.STAFF}: that one copies
     * QVET's staff ficha into the `qvet_staff` mirror; this one refreshes each collaborator's
     * `qvetAliases` from the fichas they are linked to. It ran nightly and was not in the panel.
     *
     * 🚨 **It only ever ADDS.** A name already stored stays even when QVET changed it: the old
     * sales are written with that name, and removing it would leave them with no owner.
     */
})(ReportType || (exports.ReportType = ReportType = {}));
/**
 * Sub-task status within a batch job
 */
var SubTaskStatus;
(function (SubTaskStatus) {
    SubTaskStatus["PENDING"] = "pending";
    SubTaskStatus["IN_PROGRESS"] = "in_progress";
    SubTaskStatus["COMPLETED"] = "completed";
    SubTaskStatus["FAILED"] = "failed";
    SubTaskStatus["SKIPPED"] = "skipped";
})(SubTaskStatus || (exports.SubTaskStatus = SubTaskStatus = {}));
/**
 * Mapping of report types to their parameter type
 */
exports.REPORT_PARAM_TYPES = {
    [ReportType.PURCHASES]: 'date_range',
    [ReportType.MOVEMENTS]: 'date_range',
    [ReportType.TRANSFERS]: 'date_range',
    [ReportType.COLLECTIONS]: 'date_range',
    [ReportType.CASH_CLOSINGS]: 'date_range',
    [ReportType.CASH_FLOWS]: 'date_range',
    [ReportType.PURCHASE_INVOICES]: 'date_range',
    [ReportType.DELIVERY_NOTES]: 'date_range',
    [ReportType.PURCHASE_ORDERS]: 'date_range',
    [ReportType.INVENTORY_SNAPSHOT]: 'snapshot',
    [ReportType.CLIENTS]: 'no_params',
    [ReportType.CATALOG]: 'no_params',
    [ReportType.SUPPLIERS]: 'no_params',
    [ReportType.COLLECTION_DETAILS]: 'date_range',
    [ReportType.SALE_DETAILS]: 'date_range',
    [ReportType.PAYABLES]: 'date_range',
    [ReportType.STAFF]: 'no_params',
    [ReportType.PETS]: 'no_params',
    [ReportType.VISITS]: 'date_range',
    [ReportType.VISIT_DIAGNOSES]: 'date_range',
    [ReportType.SECTIONS]: 'no_params',
    [ReportType.WAREHOUSES]: 'no_params',
    [ReportType.CATALOGS]: 'no_params',
};
/**
 * Job execution status
 */
var JobStatus;
(function (JobStatus) {
    JobStatus["PENDING"] = "pending";
    JobStatus["PROCESSING"] = "processing";
    JobStatus["COMPLETED"] = "completed";
    JobStatus["FAILED"] = "failed";
    JobStatus["CANCELLED"] = "cancelled";
})(JobStatus || (exports.JobStatus = JobStatus = {}));
/**
 * Current step within a sync job
 */
var JobStep;
(function (JobStep) {
    JobStep["DOWNLOAD"] = "download";
    JobStep["PARSE"] = "parse";
    JobStep["TRANSFORM"] = "transform";
    JobStep["SYNC"] = "sync";
})(JobStep || (exports.JobStep = JobStep = {}));
/**
 * Human-readable labels for job types (Spanish for UI)
 */
exports.JOB_TYPE_LABELS = {
    [JobType.SYNC_PURCHASES]: 'Sincronizar Compras',
    [JobType.SYNC_INVENTORY]: 'Sincronizar Inventario',
    [JobType.SYNC_TRANSFERS]: 'Sincronizar Traspasos',
    [JobType.SYNC_MOVEMENTS]: 'Sincronizar Movimientos',
    [JobType.SYNC_COLLECTIONS]: 'Sincronizar Cobros',
    [JobType.SYNC_CASH_CLOSINGS]: 'Sincronizar Cierres de Caja',
    [JobType.SYNC_CASH_FLOWS]: 'Sincronizar Entradas/Salidas',
    [JobType.SYNC_PURCHASE_INVOICES]: 'Sincronizar Facturas Compra',
    [JobType.SYNC_DELIVERY_NOTES]: 'Sincronizar Albaranes',
    [JobType.SYNC_PURCHASE_ORDERS]: 'Sincronizar Pedidos',
    [JobType.SYNC_CATALOG]: 'Sincronizar Catálogo',
    [JobType.SYNC_SUPPLIERS]: 'Sincronizar Proveedores',
    [JobType.SYNC_CLIENTS]: 'Sincronizar Clientes',
    [JobType.SYNC_INVENTORY_SNAPSHOT]: 'Sincronizar Snapshot Inventario',
    [JobType.SYNC_COLLECTION_DETAILS]: 'Sincronizar Cobros Detalle',
    [JobType.SYNC_SALE_DETAILS]: 'Sincronizar Ventas',
    [JobType.SYNC_PAYABLES]: 'Sincronizar Cuentas por Pagar',
    [JobType.SYNC_STAFF]: 'Sincronizar Personal',
    [JobType.SYNC_PETS]: 'Sincronizar Mascotas',
    [JobType.SYNC_VISITS]: 'Sincronizar Visitas',
    [JobType.SYNC_VISIT_DIAGNOSES]: 'Sincronizar Diagnósticos de visitas',
    [JobType.SYNC_SECTIONS]: 'Sincronizar secciones, familias y subfamilias',
    [JobType.SYNC_WAREHOUSES]: 'Sincronizar Almacenes',
    [JobType.SYNC_CATALOGS]: 'Sincronizar Catálogos de parámetros',
    [JobType.SYNC_BATCH]: 'Sincronización por Lotes',
    [JobType.SYNC_ALL]: 'Sincronizar Todo',
    [JobType.SYNC_FACTURAMA_PRODUCTS]: 'Sincronizar Productos Facturama',
    [JobType.SYNC_GOOGLE_CONTACTS]: 'Sincronizar Contactos Google',
    [JobType.PUSH_CATALOG_TO_QVET]: 'Escritura de catálogo en QVET',
    [JobType.SYNC_CATALOG_FULL]: 'Sincronización completa del catálogo',
    [JobType.SYNC_SUPPLIER_PRICES]: 'Actualizar Precios de Proveedor',
    [JobType.SYNC_SITPV_PRESCRIPTIONS]: 'Sincronizar recetas SITPV',
};
/**
 * Human-readable labels for report types (Spanish for UI)
 */
exports.REPORT_TYPE_LABELS = {
    [ReportType.PURCHASES]: 'Compras',
    [ReportType.MOVEMENTS]: 'Movimientos',
    [ReportType.TRANSFERS]: 'Traspasos',
    [ReportType.COLLECTIONS]: 'Cobros',
    [ReportType.CASH_CLOSINGS]: 'Cierres de Caja',
    [ReportType.CASH_FLOWS]: 'Entradas/Salidas',
    [ReportType.PURCHASE_INVOICES]: 'Facturas Compra',
    [ReportType.DELIVERY_NOTES]: 'Albaranes',
    [ReportType.PURCHASE_ORDERS]: 'Pedidos',
    [ReportType.INVENTORY_SNAPSHOT]: 'Cortes de Inventario',
    [ReportType.CLIENTS]: 'Clientes',
    [ReportType.CATALOG]: 'Catálogo',
    [ReportType.SUPPLIERS]: 'Proveedores',
    [ReportType.COLLECTION_DETAILS]: 'Cobros Detalle',
    [ReportType.SALE_DETAILS]: 'Ventas',
    [ReportType.PAYABLES]: 'Cuentas por Pagar',
    [ReportType.STAFF]: 'Personal',
    [ReportType.PETS]: 'Mascotas',
    [ReportType.VISITS]: 'Visitas',
    [ReportType.VISIT_DIAGNOSES]: 'Diagnósticos de visitas',
    [ReportType.SECTIONS]: 'Secciones, familias y subfamilias',
    [ReportType.WAREHOUSES]: 'Almacenes',
    [ReportType.CATALOGS]: 'Catálogos de parámetros',
};
/**
 * **What each report actually does to the data.** GH527 · SP10.
 *
 * > *«El tratamiento no es siempre el mismo y habría que distinguirlo.»* — Javier, 2026-09-12
 *
 * The sync panel used to group reports by **which parameters the report asks for** — by date, by
 * snapshot date, or none. That says nothing about what happens to anyone's data: measured on
 * 2026-09-12, «Proveedores» and «Clientes» sat in the same group and behave differently — one
 * writes half a ficha of a model of ours and flags what vanishes, the other replaces a mirror.
 *
 * 🚨 **`kind: 'ours'` is the one that matters.** Those syncs write into a document that also
 * holds decisions nobody else can reconstruct, so they write the `qvetSync` half **field by
 * field** and never the block whole. Verified for all five on 2026-09-12.
 *
 * Parameters still decide which inputs a form shows — that is a form concern, and stays in
 * {@link REPORT_PARAM_TYPES}.
 */
exports.QVET_REPORT_TREATMENT = {
    [ReportType.SALE_DETAILS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_sale_details', replacesOnCustomRange: true,
        description: 'Cada ticket línea a línea: artículo, cantidad, importe y quién lo vendió. Es la base de ' +
            'las comisiones y del análisis de ventas.',
    },
    [ReportType.COLLECTIONS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_collections',
        description: 'Cada cobro con su importe, forma de pago, caja y centro.',
    },
    [ReportType.COLLECTION_DETAILS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_collection_details', replacesOnCustomRange: true,
        description: 'El desglose de cada cobro por factura, que es lo que permite casar un cobro con su venta.',
    },
    [ReportType.PURCHASES]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_purchases', replacesOnCustomRange: true,
        description: 'Las recepciones de mercancía: proveedor, artículo, cantidad y coste.',
    },
    [ReportType.PURCHASE_ORDERS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_purchase_orders', replacesOnCustomRange: true,
        description: 'Los pedidos hechos a proveedor, con lo pedido y lo que falta por recibir.',
    },
    [ReportType.PURCHASE_INVOICES]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_purchase_invoices', replacesOnCustomRange: true,
        description: 'Las facturas de compra con su importe, su fecha y su proveedor.',
    },
    [ReportType.DELIVERY_NOTES]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_delivery_notes', replacesOnCustomRange: true,
        description: 'La cabecera de cada entrega: su número, el folio que puso el proveedor, si está facturada ' +
            'y con qué factura. Las líneas viven en las recepciones, que es donde está el factor de ' +
            'conversión.',
    },
    [ReportType.PAYABLES]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_payables', replacesOnCustomRange: true,
        description: 'Los vencimientos por pagar: cuánto, a quién y cuándo.',
    },
    [ReportType.MOVEMENTS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_movements',
        description: 'Todo movimiento de existencias: ventas, entradas, ajustes y traspasos. QVET no tiene ' +
            'tipo de movimiento para merma ni caducidad, así que lo que se echó a perder no sale aquí.',
    },
    [ReportType.TRANSFERS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_transfers', replacesOnCustomRange: true,
        description: 'Los traspasos entre almacenes, con origen, destino y cantidad.',
    },
    [ReportType.CASH_CLOSINGS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_cash_closings',
        description: 'Los cierres de caja: lo declarado, lo contado y el descuadre.',
    },
    [ReportType.CASH_FLOWS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_cash_flows',
        description: 'Entradas y salidas de caja que no son ni ventas ni cobros.',
    },
    [ReportType.VISITS]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_visits',
        description: 'Las visitas clínicas: mascota, fecha, centro y quién atendió.',
    },
    [ReportType.VISIT_DIAGNOSES]: {
        kind: 'mirror', removal: 'replaces-window', writes: 'qvet_visit_diagnoses',
        description: 'Los diagnósticos de cada visita, uno por renglón, con crónico y definitivo. Sólo trae ' +
            'las visitas que tienen diagnóstico; se cruzan con las visitas por su número.',
    },
    [ReportType.CLIENTS]: {
        kind: 'mirror', removal: 'marks-missing', writes: 'qvet_clients',
        description: 'La ficha del cliente: nombre, contacto, dirección y fecha de alta.',
    },
    [ReportType.PETS]: {
        kind: 'mirror', removal: 'marks-missing', writes: 'qvet_pets',
        description: 'La ficha de la mascota: especie, raza, nacimiento y de quién es.',
    },
    [ReportType.STAFF]: {
        kind: 'mirror', removal: 'marks-missing', writes: 'qvet_staff',
        description: 'La ficha del personal tal como está en QVET, y al terminar enlaza solo a cada ' +
            'colaborador cuyo nombre casa sin duda (igual, o a una o dos letras). Lo dudoso se ' +
            'deja para que alguien lo decida desde la ficha del colaborador.',
    },
    // Reads the article catalogue to know which codes exist; writes only its own snapshots.
    [ReportType.INVENTORY_SNAPSHOT]: {
        kind: 'mirror', removal: 'never-removes', writes: 'qvet_inventory_snapshots',
        description: 'Una foto de las existencias a una fecha, para el corte de inventario. No cambia el stock ' +
            'de hoy: sólo guarda cómo estaba ese día.',
    },
    [ReportType.CATALOG]: {
        kind: 'ours', removal: 'marks-missing', writes: 'catalog_items',
        description: 'La ficha del artículo: nombre, sección, familia, marca, precios y existencias por ' +
            'almacén. Escribe sólo la mitad de QVET; el tipo de uso, la política de precio y el suelo ' +
            'son tuyos y no los toca.',
    },
    [ReportType.SUPPLIERS]: {
        kind: 'ours', removal: 'marks-missing', writes: 'suppliers',
        description: 'La ficha del proveedor: nombre, contacto, condiciones de cobro y fecha de alta. El 5 %, ' +
            'la bonificación y el identificador corto son tuyos y no los toca.',
    },
    [ReportType.SECTIONS]: {
        kind: 'ours', removal: 'marks-missing', writes: 'sections',
        description: 'El árbol entero: 20 secciones, sus 167 familias y sus 449 subfamilias. Si la sección ' +
            'cuenta en el corte de inventario lo decides tú, y eso no se toca; de familias y ' +
            'subfamilias no decidimos nada, así que son catálogo puro.',
    },
    [ReportType.WAREHOUSES]: {
        kind: 'ours', removal: 'marks-missing', writes: 'warehouses',
        description: 'Los almacenes. Si el almacén cuenta en el corte de existencias lo decides tú, y eso no ' +
            'se toca.',
    },
    // Las 20 listas de parámetros en una pasada: marcas, IVA, unidades, vías, principios activos,
    // familias, subfamilias, centros… De una marca no decidimos nada, así que son clase A.
    [ReportType.CATALOGS]: {
        kind: 'catalog', removal: 'marks-missing', writes: 'qvet_catalogs',
        description: 'Las listas de valores sueltas de QVET, de una pasada: marcas, tipos de IVA, unidades, ' +
            'vías, principios activos, laboratorios, tarifas y centros. Las familias y subfamilias ' +
            'NO están aquí: van con las secciones, porque son el mismo árbol.',
    },
};
exports.QVET_SYNC_SCHEDULE = {
    // The fifteen that go every night, inside the batch.
    [ReportType.COLLECTIONS]: { kind: 'daily', withBatch: true },
    [ReportType.CASH_CLOSINGS]: { kind: 'daily', withBatch: true },
    [ReportType.CASH_FLOWS]: { kind: 'daily', withBatch: true },
    [ReportType.PURCHASE_INVOICES]: { kind: 'daily', withBatch: true },
    [ReportType.DELIVERY_NOTES]: { kind: 'daily', withBatch: true },
    [ReportType.PURCHASE_ORDERS]: { kind: 'daily', withBatch: true },
    [ReportType.TRANSFERS]: { kind: 'daily', withBatch: true },
    [ReportType.SUPPLIERS]: { kind: 'daily', withBatch: true },
    [ReportType.STAFF]: { kind: 'daily', withBatch: true },
    [ReportType.CATALOG]: { kind: 'daily', withBatch: true },
    [ReportType.PURCHASES]: { kind: 'daily', withBatch: true },
    [ReportType.COLLECTION_DETAILS]: { kind: 'daily', withBatch: true },
    [ReportType.SALE_DETAILS]: { kind: 'daily', withBatch: true },
    [ReportType.VISITS]: { kind: 'daily', withBatch: true },
    [ReportType.VISIT_DIAGNOSES]: { kind: 'daily', withBatch: true },
    [ReportType.CLIENTS]: { kind: 'daily', withBatch: true },
    [ReportType.PETS]: { kind: 'daily', withBatch: true },
    // 🚨 Movements is NOT daily. Four times a month, which leaves a ten-day gap between the 21st
    // and the 1st — and that gap is normal, not a fault.
    [ReportType.MOVEMENTS]: { kind: 'days-of-month', daysOfMonth: [1, 7, 16, 21], withBatch: true },
    // Vencimientos: twice a month.
    // Daily since 2026-09-18 (hvp-backend#553): the payments screen reads «paid» from here, and it
    // is 439 rows in 4 s.
    [ReportType.PAYABLES]: { kind: 'daily', withBatch: true },
    // The month-end cut, on the 1st, over the last day of the previous month.
    [ReportType.INVENTORY_SNAPSHOT]: { kind: 'days-of-month', daysOfMonth: [1], withBatch: true },
    // The three that run every night BEFORE the batch, as their own steps: the parameter lists and
    // the catalogues, which everything after them resolves names with. (STAFF_LINKS used to be a
    // fourth, after the batch; since 2026-09-13 linking collaborators is part of STAFF itself.)
    [ReportType.CATALOGS]: { kind: 'daily', withBatch: false },
    [ReportType.SECTIONS]: { kind: 'daily', withBatch: false },
    [ReportType.WAREHOUSES]: { kind: 'daily', withBatch: false },
};
/**
 * **The longest a report can legitimately go between runs**, in days — which is what freshness
 * has to be judged against, not the average gap.
 *
 * For `[1, 7, 16, 21]` the gaps are 6, 9, 5 and 10 days: the threshold is **10**, because a
 * report last read on the 21st is perfectly healthy on the 30th.
 */
function qvetExpectedEveryDays(reportType) {
    const schedule = exports.QVET_SYNC_SCHEDULE[reportType];
    if (!schedule || schedule.kind === 'daily')
        return 1;
    if (schedule.kind === 'manual')
        return Number.POSITIVE_INFINITY;
    const days = [...(schedule.daysOfMonth ?? [1])].sort((a, b) => a - b);
    // 31 keeps the wrap-around gap honest for the long months, which is the generous reading —
    // and a generous threshold is the right bias: a false red costs more than a late amber.
    const EN_UN_MES = 31;
    let mayor = 0;
    for (let i = 0; i < days.length; i += 1) {
        const siguiente = i + 1 < days.length ? days[i + 1] : days[0] + EN_UN_MES;
        mayor = Math.max(mayor, siguiente - days[i]);
    }
    return mayor;
}
const MESES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
const ordinales = (days) => {
    if (days.length === 1)
        return `el día ${days[0]}`;
    return `los días ${days.slice(0, -1).join(', ')} y ${days[days.length - 1]}`;
};
/** «Cada noche» · «Los días 1, 7, 16 y 21 de cada mes» · «Sólo a mano». For the screen. */
function qvetScheduleLabel(reportType) {
    const schedule = exports.QVET_SYNC_SCHEDULE[reportType];
    if (!schedule || schedule.kind === 'manual')
        return 'Sólo a mano';
    if (schedule.kind === 'daily')
        return 'Cada noche';
    return `Cada mes, ${ordinales(schedule.daysOfMonth ?? [1])}`;
}
const fechaLarga = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d)
        return iso;
    return `${d} de ${MESES[m - 1]} de ${y}`;
};
function qvetSyncPreview(reportType, params = {}) {
    const t = exports.QVET_REPORT_TREATMENT[reportType];
    const p = params;
    let window;
    if (typeof p.snapshotDate === 'string') {
        window = `Las existencias tal como estaban el ${fechaLarga(p.snapshotDate)}`;
    }
    else if (typeof p.startDate === 'string' && typeof p.endDate === 'string') {
        window = `Del ${fechaLarga(p.startDate)} al ${fechaLarga(p.endDate)}`;
    }
    else if (typeof p.year === 'number') {
        window =
            typeof p.month === 'number' && p.month >= 1 && p.month <= 12
                ? `Todo ${MESES[p.month - 1]} de ${p.year}`
                : `Todo el año ${p.year}, de enero a diciembre`;
    }
    else {
        window = 'La lista entera, tal como esté hoy en QVET';
    }
    // 🚨 Si de verdad va a borrar depende del MODO, no sólo del informe: la limpieza corre en
    // Año/Mes, y con un rango de fechas suelto sólo en los cuatro que se apuntaron.
    const porRangoSuelto = typeof p.startDate === 'string' && typeof p.endDate === 'string';
    const vaABorrar = !porRangoSuelto || t.replacesOnCustomRange === true;
    const effect = t.removal === 'replaces-window'
        ? vaABorrar
            ? `Borra en ${t.writes} lo que hubiera de esas fechas y lo reescribe con lo que diga ` +
                `QVET, así que lo que allí se haya borrado desaparece también aquí. Si el informe ` +
                `llega con menos de la mitad de lo que ya hay, no borra nada y avisa`
            : `Añade y actualiza en ${t.writes}. Con un rango de fechas suelto este informe NO ` +
                `borra: sólo limpia el periodo cuando se le pide un año o un mes completo`
        : t.removal === 'never-removes'
            ? t.kind === 'ours'
                ? `Actualiza en ${t.writes} sólo la mitad que viene de QVET, campo a campo. No borra nada, y no toca tu mitad`
                : `Añade y actualiza en ${t.writes}. No borra nada: lo retirado en QVET se queda aquí`
            : t.kind === 'ours'
                ? `Actualiza en ${t.writes} la mitad que viene de QVET; lo que QVET ya no tiene se borra, salvo lo que algo nuestro usa, que se marca. No toca tu mitad`
                : `Añade y actualiza en ${t.writes}, y borra lo que QVET ya no tiene, salvo lo que algo nuestro usa, que se marca. Si el informe trae menos de la mitad de lo guardado, no borra: marca y avisa`;
    return { window, effect };
}
exports.QVET_SYNC_WINDOW_LABELS = {
    'sin-fechas': 'sin periodo',
    'hasta-10-dias': 'unos días',
    'un-mes': 'un mes',
    'pocos-meses': 'dos a cuatro meses',
    'medio-anyo': 'medio año',
    'casi-un-anyo': 'casi un año',
};
/**
 * **Cuántos días de datos abarca de verdad una ejecución** — que no es lo mismo que los que pide.
 *
 * 🚨 **«Año 2026» lanzado en enero son 30 días de datos; en septiembre, 255.** Lo cazó Javier el
 * 2026-09-12 mirando la fila: *«quizá si lo mezclas con el inicio de año, pero la realidad es que
 * ahora en septiembre no creo que tarde menos de 4 min»*. Tenía razón, y la primera versión de
 * este cubo metía las dos en el mismo saco de «un año», así que la mediana salía **103 s** cuando
 * la de verdad, para septiembre, es **207 s**.
 *
 * Remedido sobre las 70 pasadas de `MOVEMENTS`, ya separadas por lo que abarcan:
 *
 * | Abarca | n | Mediana |
 * |---|---:|---:|
 * | 9-12 meses | 11 | **207 s** (las dos últimas, 314 y 333) |
 * | 5-8 meses | 9 | 128 s |
 * | 2-4 meses | 10 | 63 s |
 * | Un mes | 24 | 32 s |
 * | Hasta 10 días | 16 | 38 s |
 *
 * @param referencia  hasta cuándo hay datos. Para una ejecución pasada es **su fecha**; para
 *                    predecir la siguiente, hoy. Sin esto, una pasada de enero y una de
 *                    septiembre del mismo año se cuentan igual, que es el fallo que esto arregla.
 */
function qvetSyncWindow(params = {}, referencia = new Date()) {
    const p = params;
    let dias = null;
    if (typeof p.startDate === 'string' && typeof p.endDate === 'string') {
        const desde = new Date(p.startDate).getTime();
        const hasta = new Date(p.endDate).getTime();
        if (!Number.isNaN(desde) && !Number.isNaN(hasta))
            dias = Math.round((hasta - desde) / 86400000);
    }
    else if (typeof p.year === 'number') {
        if (typeof p.month === 'number' && p.month >= 1 && p.month <= 12) {
            dias = 30;
        }
        else {
            // Del 1 de enero a hoy, o al fin de año si ya pasó: los días que QVET puede devolver.
            const enero = Date.UTC(p.year, 0, 1);
            const tope = Math.min(referencia.getTime(), Date.UTC(p.year + 1, 0, 1));
            dias = Math.max(1, Math.round((tope - enero) / 86400000));
        }
    }
    if (dias === null)
        return 'sin-fechas';
    if (dias <= 10)
        return 'hasta-10-dias';
    if (dias <= 40)
        return 'un-mes';
    if (dias <= 120)
        return 'pocos-meses';
    if (dias <= 250)
        return 'medio-anyo';
    return 'casi-un-anyo';
}
exports.QVET_FRESHNESS_LABELS = {
    fresh: 'Al día',
    stale: 'Se está quedando atrás',
    old: 'Lleva mucho sin leerse',
    unknown: 'Sin lectura',
};
/**
 * Judge a report's freshness from its last read.
 *
 * @param reportType  which report — the threshold is per report, never one for everybody.
 * @param lastSyncAt  the last read, or nothing if it was never read.
 * @param now         injectable so a test does not depend on the clock.
 */
function qvetFreshness(reportType, lastSyncAt, now = new Date()) {
    if (!lastSyncAt)
        return 'unknown';
    const read = lastSyncAt instanceof Date ? lastSyncAt : new Date(lastSyncAt);
    if (Number.isNaN(read.getTime()))
        return 'unknown';
    const expected = qvetExpectedEveryDays(reportType);
    const ageDays = (now.getTime() - read.getTime()) / 86400000;
    if (ageDays <= expected * 2)
        return 'fresh';
    if (ageDays <= expected * 4)
        return 'stale';
    return 'old';
}
/** Spanish, for the screen. */
exports.QVET_SYNC_KIND_LABELS = {
    mirror: 'Copias de QVET',
    catalog: 'Listas de valores',
    ours: 'Nuestros, con el espejo dentro',
};
/**
 * 🚨 **Rewritten on 2026-09-12 because the old ones were jargon.**
 *
 * > *«Sigo sin tener claro qué es "Reemplaza el periodo entero".»* — Javier
 *
 * He was right, and it is the failure this whole contract exists to prevent: the panel used to
 * group by parameters because nothing said what a sync does to the data, and then the thing that
 * said it said it in words only its author understood. A label that has to be explained has not
 * done its job. Say the verb: **borra**, **añade**, **marca**.
 */
exports.QVET_SYNC_REMOVAL_LABELS = {
    'replaces-window': 'Borra y reescribe esas fechas',
    'never-removes': 'Sólo añade y actualiza; nunca borra',
    'marks-missing': 'Borra lo que QVET ya no tiene; marca lo que algo nuestro usa',
};
/** The paragraph behind each label, for a tooltip. Says what happens and what protects you. */
exports.QVET_SYNC_REMOVAL_HELP = {
    'replaces-window': 'Borra de nuestra copia todo lo que hubiera con fechas de ese periodo y escribe de nuevo lo ' +
        'que traiga QVET. Es la única forma de que algo borrado allí desaparezca también aquí. ' +
        'Con una red: si el informe vuelve con menos de la mitad de lo que ya teníamos, no borra ' +
        'nada y lo avisa — un informe que llega corto no se distingue de un borrado de verdad, y ' +
        'ya pasó una vez. Ojo: sólo borra si pides Año/Mes; con un rango de fechas suelto, casi ' +
        'todos se limitan a añadir y actualizar.',
    'never-removes': 'Añade lo nuevo y actualiza lo que cambió, y nunca quita nada. Lo que QVET retire se queda ' +
        'aquí a propósito, para que un documento viejo pueda seguir nombrándolo.',
    'marks-missing': 'Lo que ya no viene en el informe se borra de nuestra copia, porque en QVET ya no existe. ' +
        'Salvo lo que algo nuestro referencia —una cuenta de facturación, una receta, un estudio, ' +
        'un artículo—, que se marca como retirado y se conserva: QVET no ve nuestras claves. Con ' +
        'una red: si el informe trae menos de la mitad de lo guardado, no borra nada, lo marca y lo ' +
        'avisa; para un borrado así de grande hay que confirmarlo desde el panel.',
};
/**
 * Human-readable labels for sub-task status (Spanish for UI)
 */
exports.SUB_TASK_STATUS_LABELS = {
    [SubTaskStatus.PENDING]: 'Pendiente',
    [SubTaskStatus.IN_PROGRESS]: 'En proceso',
    [SubTaskStatus.COMPLETED]: 'Completado',
    [SubTaskStatus.FAILED]: 'Fallido',
    [SubTaskStatus.SKIPPED]: 'Omitido',
};
/**
 * Human-readable labels for job steps (Spanish for UI)
 */
exports.JOB_STEP_LABELS = {
    [JobStep.DOWNLOAD]: 'Descargando',
    [JobStep.PARSE]: 'Procesando archivo',
    [JobStep.TRANSFORM]: 'Transformando datos',
    [JobStep.SYNC]: 'Guardando en base de datos',
};
/**
 * Human-readable labels for job status (Spanish for UI)
 */
exports.JOB_STATUS_LABELS = {
    [JobStatus.PENDING]: 'Pendiente',
    [JobStatus.PROCESSING]: 'En proceso',
    [JobStatus.COMPLETED]: 'Completado',
    [JobStatus.FAILED]: 'Fallido',
    [JobStatus.CANCELLED]: 'Cancelado',
};
